import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import i18n from 'src/utils/translate';
import { pluralize } from 'src/utils/string';
import signupContext from 'src/constants/signupContext';
import Response from './response';
import Button from 'src/components/buttons/button';
import { Reply, State } from 'src/types';
import { titleMedium, textLight } from 'styles-js/mixins/typography';
import restructurePostResponses from 'src/utils/dataStructure/restructurePostResponses';
import PrivatePostMessage from './privatePostMessage';
import { useIsCommunityMember } from 'src/utils/context';


export default function Responses() {
  const post = useSelector(({pages}: State) => pages.singlePost.post);
  const [responses, isReversed, doReverse] = useRestructuredResponses();
  const isCommunityMember = useIsCommunityMember();

  const isPrivate = post.isPrivate && !isCommunityMember;

  return (
    <Fragment>
      <Header id="post-responses">
        {post?.totalResponses > 0 &&
          <Fragment>
            <ReplyCount>
              {pluralize(i18n.t('%1$s Reply'), i18n.t('%1$s Replies'), post?.totalResponses)}
            </ReplyCount>
            {!isPrivate &&
              <SortButtons>
                <Button
                  className={isReversed ? '' : 'selected'}
                  type="link"
                  onClick={doReverse}
                >
                  {i18n.t('oldest')}
                </Button>
                <span>•</span>
                <Button
                  className={isReversed ? 'selected' : ''}
                  type="link"
                  onClick={doReverse}
                >
                  {i18n.t('newest')}
                </Button>
              </SortButtons>
            }
          </Fragment>
        }
      </Header>

      {!isPrivate
        ? responses.map((response: Reply) =>
          <Response
            indent={response.indent}
            key={response.id}
            response={response}
            signupContext={{section: signupContext.section.POST_REPLY}}
            superPost={post}
          />,
        ) : (
          <PrivatePostMessage>
            <p>{i18n.t('You need to be a member of this community to see hidden responses.')}</p>
          </PrivatePostMessage>
        )
      }
    </Fragment>
  );
}

const useRestructuredResponses = () => {
  const {id, responses} = useSelector(({pages}: State) => pages.singlePost.post);
  const memoizedResponses = useMemo(() => restructurePostResponses(id, responses), [id, responses]);
  const [restructuredResponses, setRestructuredResponses] = useState(memoizedResponses);

  useEffect(() => {
    setRestructuredResponses(memoizedResponses);
  }, [memoizedResponses]);

  const [isReversed, setIsReversed] = useState(false);
  const doReverse = useCallback(() => {
    setRestructuredResponses(restructuredResponses.reverse());
    setIsReversed(!isReversed);
  }, [restructuredResponses, isReversed]);


  return [restructuredResponses, isReversed, doReverse ];
};

const Header = styled.div`
  margin-bottom: 5px;
  height: 40px;
  padding-bottom: 10px;
  border-bottom: 1px solid ${({theme}) => theme.colorGreyLight};
  display: flex;
  justify-content: space-between;
`;

const ReplyCount = styled.div`
  ${titleMedium}
`;

const SortButtons = styled.div`
  ${textLight}
  display: flex;
  align-items: center;
  button {
    ${textLight}
    margin: 0;
    padding: 3px 6px;
    transition: none;
    &:focus,
    &:hover {
      ${textLight}
    }
    &.selected {
      color: ${({theme}) => theme.colorGreyDark};
      font-weight: ${({theme}) => theme.fontWeightBold};
    }
  }
`;
